import React, { useState } from "react";
import { getSidebarMenuConfig } from "./config/menu.config";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarLogo from "./SidebarLogo";
import SidebarMenuItem from "./components/SidebarMenuItem";
import "./Sidebar.css";
import { openItemOnSideBarClick } from "./utils/openItemOnSideBarClick";

interface MenuItem {
	key: string;
	label: string;
	icon: React.ReactNode;
	route: string;
	children?: MenuItem[];
	tooltip?: string;
}

const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const menu = getSidebarMenuConfig(t);
	const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

	const toggleMenu = (menuKey: string) => {
		setExpandedMenus(prev => (prev.includes(menuKey) ? prev.filter(key => key !== menuKey) : [...prev, menuKey]));
	};

	const isMenuExpanded = (menuKey: string) => expandedMenus.includes(menuKey);
	const isActiveRoute = (route: string) => {
		if (route === "/course-builder") {
			return location.pathname.startsWith("/course-builder");
		}
		return location.pathname === route;
	};
	const isChildActive = (children: MenuItem[]) => {
		return children?.some(child => isActiveRoute(child.route));
	};

	const renderMenuItems = (items: MenuItem[], level: number = 0) =>
		items.map(item => {
			const hasChildren = Boolean(item.children && item.children.length > 0);
			const isExpanded = isMenuExpanded(item.key);
			const isActive = isActiveRoute(item.route);
			const hasActiveChild = hasChildren && isChildActive(item.children || []);
			return (
				<SidebarMenuItem
					key={item.key}
					item={item}
					level={level}
					isExpanded={isExpanded}
					isActive={isActive}
					hasActiveChild={hasActiveChild}
					onClick={() => openItemOnSideBarClick(item, hasChildren, toggleMenu, navigate)}
					toggleMenu={toggleMenu}
					renderChildren={children => renderMenuItems(children || [], level + 1)}
				/>
			);
		});

	return (
		<aside className="sidebar-shell sidebar-container sidebar-bg sidebar-expanded fixed left-0 top-0 z-20 flex h-screen flex-col border-r border-white/10 text-white">
			<SidebarLogo />

			<div className="sidebar-nav-area flex min-h-0 flex-1 flex-col">
				<nav className="sidebar-nav w-full flex-1 overflow-y-auto overflow-x-hidden px-0 pt-0 transition-all duration-300">
					<ul className="mb-6 w-full space-y-0">{renderMenuItems(menu)}</ul>
				</nav>
			</div>
		</aside>
	);
};

export default Sidebar;
